import React, { useEffect, useCallback } from 'react';
import Button from './Button';

/**
 * Modal 컴포넌트
 *
 * @param {boolean} isOpen - 모달 표시 여부
 * @param {function} onClose - 닫기 핸들러
 * @param {string} title - 모달 제목
 * @param {string} size - sm, md, lg, xl, full
 * @param {boolean} closeOnOverlay - 오버레이 클릭 시 닫기
 * @param {boolean} closeOnEscape - ESC 키로 닫기
 * @param {boolean} showClose - 닫기 버튼 표시
 * @param {React.ReactNode} footer - 푸터 내용
 * @param {React.ReactNode} children - 모달 내용
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  size = 'md',
  closeOnOverlay = true,
  closeOnEscape = true,
  showClose = true,
  footer,
  children
}) => {
  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape' && closeOnEscape) {
      onClose();
    }
  }, [closeOnEscape, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && closeOnOverlay) {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  const getSizeStyle = () => {
    switch (size) {
      case 'sm': return 'max-w-[360px]';
      case 'lg': return 'max-w-[640px]';
      case 'xl': return 'max-w-[800px]';
      case 'full': return 'w-[calc(100%-40px)] h-[calc(100%-40px)] max-w-none max-h-none';
      default: return 'max-w-[480px]';
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000] p-5 animate-fadeIn"
      onClick={handleOverlayClick}
    >
      <div
        className={`bg-white rounded-2xl shadow-2xl max-h-[calc(100vh-40px)] flex flex-col w-full animate-slideUp ${getSizeStyle()}`}
        role="dialog"
        aria-modal="true"
      >
        {(title || showClose) && (
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
            {title && <h2 className="text-base font-semibold text-gray-900 m-0">{title}</h2>}
            {showClose && (
              <button
                className="flex items-center justify-center w-7 h-7 bg-transparent border-none rounded-md text-gray-400 text-sm cursor-pointer transition-all duration-150 hover:bg-gray-100 hover:text-gray-900"
                onClick={onClose}
                aria-label="닫기"
              >
                ✕
              </button>
            )}
          </div>
        )}
        <div className="p-5 overflow-y-auto flex-1">
          {children}
        </div>
        {footer && (
          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * ConfirmModal 컴포넌트 - 확인 모달
 *
 * @param {boolean} isOpen - 모달 표시 여부
 * @param {function} onClose - 닫기 핸들러
 * @param {function} onConfirm - 확인 핸들러
 * @param {string} title - 제목
 * @param {string} message - 메시지
 * @param {string} confirmText - 확인 버튼 텍스트
 * @param {string} cancelText - 취소 버튼 텍스트
 * @param {string} variant - 확인 버튼 스타일 (primary, danger)
 * @param {boolean} loading - 로딩 상태
 */
export const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = '확인',
  message,
  confirmText = '확인',
  cancelText = '취소',
  variant = 'primary',
  loading = false
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {cancelText}
          </Button>
          <Button variant={variant} onClick={onConfirm} loading={loading}>
            {confirmText}
          </Button>
        </>
      }
    >
      <p className="text-sm text-gray-500 leading-relaxed m-0">{message}</p>
    </Modal>
  );
};

/**
 * AlertModal 컴포넌트 - 알림 모달
 */
export const AlertModal = ({
  isOpen,
  onClose,
  title = '알림',
  message,
  buttonText = '확인'
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <Button variant="primary" onClick={onClose}>
          {buttonText}
        </Button>
      }
    >
      <p className="text-sm text-gray-500 leading-relaxed m-0">{message}</p>
    </Modal>
  );
};

export default Modal;
